import type { CompanyAccountInput } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { createCompanyAccount, deleteCompanyAccount, retireCompanyAccount, updateCompanyAccount, type CompanyAccountView } from '../company-accounts.api';

/** Adds, edits, retires and deletes accounts. A refusal while saving stays inline in the drawer; a retire or delete failure toasts. Each success toasts and reloads the list. */
export function useCompanyAccountActions(reload: () => Promise<void>) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  /** Returns whether it saved, so the drawer stays open with its error showing when it did not. */
  async function save(editing: CompanyAccountView | undefined, input: CompanyAccountInput): Promise<boolean> {
    setError(null);
    setSaving(true);
    try {
      if (editing) await updateCompanyAccount(editing.id, input);
      else await createCompanyAccount(input);
      toast.success(`Account ${input.name} ${editing ? 'updated' : 'added'}`);
      await reload();
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not save the account — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function change(account: CompanyAccountView, action: (id: string) => Promise<unknown>, pastTense: string, verb: string): Promise<void> {
    try {
      await action(account.id);
      toast.success(`Account ${account.name} ${pastTense}`);
      await reload();
    } catch (err) {
      toast.error(apiErrorMessage(err) ?? `Could not ${verb} ${account.name} — try again.`);
    }
  }

  return {
    error,
    saving,
    save,
    retire: (account: CompanyAccountView) => change(account, retireCompanyAccount, 'retired', 'retire'),
    remove: (account: CompanyAccountView) => change(account, deleteCompanyAccount, 'deleted', 'delete'),
    resetError: () => setError(null),
  };
}
